import type {ShellAccount} from '@wandb/forge-shell-contract';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useShellAccountContext} from '../../../shell/ShellAccountContext';
import {isMountedInShell} from '../../../shellRemote';
import {AccountSelectorContext} from './AccountSelectorContextProvider';
import {AccountSelectorContextProviderWrapper} from './AccountSelectorContextProviderWrapper';
import {getCanSeeOrgInURL, getUrlAccount} from './AccountSelectorUtils';
import {ShellAdminModals} from './ShellAdminModals';
import {type Account, AccountType} from './types';
import {useUrlAccountInputs} from './useUrlAccountInputs';

export function findShellAccount(
  shellAccount: ShellAccount,
  accountList: Account[]
): Account | undefined {
  return (
    accountList.find(account => account.id === shellAccount.id) ??
    accountList.find(
      account =>
        account.name === shellAccount.name &&
        (shellAccount.kind === 'personal'
          ? account.accountType === AccountType.Personal
          : account.accountType !== AccountType.Personal)
    )
  );
}

// Under the Forge shell the shell owns account selection: this provider
// broadcasts which account the URL names, reads the selection back from the
// mount props, and never touches storage.
export const ShellAccountSelectorContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => {
  const {
    selectedAccount: shellAccount,
    broadcastUrlAccount,
    selectAccount: shellSelectAccount,
  } = useShellAccountContext();
  const {
    viewer,
    accountList,
    isAccountListLoading,
    userInfo,
    orgName,
    entityName,
    orgQuery,
    entityQuery,
    personalEntityUserID,
    isAdminMode,
    isProfilePath,
    isLoaded,
  } = useUrlAccountInputs();

  // The account this page's URL names; undefined until resolution inputs load.
  const urlAccount = useMemo(
    () =>
      isLoaded && viewer != null
        ? getUrlAccount({
            selectedAccount: null,
            lastSelectedAccount: null,
            isProfilePath,
            isAdminMode,
            viewer,
            accountList,
            userInfo,
            orgName,
            entityName,
            orgQuery,
            entityQuery,
            personalEntityUserID,
          })
        : undefined,
    [
      isLoaded,
      viewer,
      isProfilePath,
      isAdminMode,
      accountList,
      userInfo,
      orgName,
      entityName,
      orgQuery,
      entityQuery,
      personalEntityUserID,
    ]
  );

  // Broadcast the URL account once per payload so the shell can apply its
  // precedence (URL > header pick > storage) without an extra query.
  const lastBroadcastRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (broadcastUrlAccount == null || urlAccount === undefined) {
      return;
    }
    const payload: ShellAccount | null =
      urlAccount?.id != null
        ? {
            id: urlAccount.id,
            name: urlAccount.name,
            kind:
              urlAccount.accountType === AccountType.Personal
                ? 'personal'
                : 'organization',
            ...(urlAccount.isDummyAccount ? {isDummyAccount: true} : {}),
          }
        : null;
    const key = JSON.stringify(payload);
    if (lastBroadcastRef.current === key) {
      return;
    }
    lastBroadcastRef.current = key;
    broadcastUrlAccount(payload);
  }, [broadcastUrlAccount, urlAccount]);

  // The shell's selection mapped back to an app Account. A member account
  // resolves against the viewer's list; a dummy we broadcast resolves to the
  // URL account we already built (which carries plan info the shell lacks).
  const shellResolvedAccount = useMemo((): Account | null => {
    if (shellAccount == null) {
      return null;
    }
    if (!shellAccount.isDummyAccount) {
      return findShellAccount(shellAccount, accountList) ?? null;
    }
    if (urlAccount?.id === shellAccount.id) {
      return urlAccount;
    }
    return null;
  }, [shellAccount, accountList, urlAccount]);

  const [override, setOverride] = useState<{
    forShellAccountId: string | undefined;
    account: Account | null;
  } | null>(null);
  // The shell owns selection and storage, so a local pick is only a
  // render-level override keyed by the shell account it was made against.
  const setSelectedAccount = useCallback(
    (account: Account | null) => {
      // Member accounts also go to the shell so the pick survives a redirect;
      // the override still applies immediately so the app does not wait for
      // the shell to round-trip the selection.
      if (
        account?.id != null &&
        !account.isDummyAccount &&
        shellSelectAccount != null
      ) {
        shellSelectAccount({
          id: account.id,
          name: account.name,
          kind:
            account.accountType === AccountType.Personal
              ? 'personal'
              : 'organization',
        });
      }
      setOverride({forShellAccountId: shellAccount?.id, account});
    },
    [shellAccount?.id, shellSelectAccount]
  );
  const overrideApplies =
    override != null && override.forShellAccountId === shellAccount?.id;
  const selectedAccount = overrideApplies
    ? override.account
    : // A shell without `broadcastUrlAccount` never hears the URL broadcast, so this
      // provider applies the URL account itself under that rollout gap.
      broadcastUrlAccount == null
      ? (urlAccount ?? shellResolvedAccount)
      : shellResolvedAccount;

  const canSeeOrgInURL = isLoaded
    ? getCanSeeOrgInURL({
        selectedAccount: null,
        lastSelectedAccount: null,
        isProfilePath,
        isAdminMode,
        viewer: viewer!,
        accountList,
        userInfo,
        orgName,
        entityName,
        orgQuery,
        entityQuery,
      })
    : true;

  const value = useMemo(
    () => ({
      selectedAccount: isLoaded ? selectedAccount : null,
      setSelectedAccount,
      lastNonImpersonatedAccount: null,
      setLastNonImpersonatedAccount: () => {},
      canSeeOrgInURL,
      accountList,
      isAccountListLoading,
    }),
    [
      accountList,
      canSeeOrgInURL,
      isAccountListLoading,
      isLoaded,
      selectedAccount,
      setSelectedAccount,
    ]
  );

  return (
    <AccountSelectorContext.Provider value={value}>
      {children}
    </AccountSelectorContext.Provider>
  );
};

export const ShellAccountSelectorContextProviderWrapper: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => (
  <ShellAccountSelectorContextProvider>
    <ShellAdminModals />
    {children}
  </ShellAccountSelectorContextProvider>
);

export const AppAccountSelectorProvider: React.FC<{
  children?: React.ReactNode;
}> = ({children}) =>
  isMountedInShell() ? (
    <ShellAccountSelectorContextProviderWrapper>
      {children}
    </ShellAccountSelectorContextProviderWrapper>
  ) : (
    <AccountSelectorContextProviderWrapper>
      {children}
    </AccountSelectorContextProviderWrapper>
  );
